import {
  WSElement,
  WSElementProps,
  WSEmptyState,
  WSList,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import {
  ListFormsSortParameterValue,
  PayableRow,
  PayableStatus,
  SearchPayableRowsFilterParameter
} from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo, useRef, useState } from "react";
import { Route } from "react-router-dom";
import { CompletedDeductionDetails } from "../../../../Invoices/screens/deductions/CompletedDuductionDetails";
import { ScheduledDeductionDetails } from "../../../../Invoices/screens/deductions/ScheduledDuductionDetails";
import {
  PATH_PAYMENT_HISTORY,
  routeHelpersClientPayableDetails
} from "../../paths";
import { ClientPayableDetails } from "../ClientPayableDetails/ClientPayableDetails";
import { ClientPayablesSection } from "./ClientPayablesSection";

const UNPAID_STATUSES = [PayableStatus.OPENED, PayableStatus.PENDING];
const PAID_STATUSES = [PayableStatus.PAID, PayableStatus.PAYMENT_IN_TRANSIT];

const baseFilter: SearchPayableRowsFilterParameter = {
  metadata_invoiceType: { ne: "approvedInvoicesPayment" },
  metadata_creationSource: { ne: "personalPaylink" }
};

type Props = WSElementProps;

export const ClientPayablesList: React.FC<Props> = props => {
  const goToPayableDetails = routeHelpersClientPayableDetails.useGoTo();

  const startOfToday = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const filterOverdue = useMemo(
    () => ({
      ...baseFilter,
      status: { anyOf: UNPAID_STATUSES },
      dueDate: { lt: startOfToday }
    }),
    [startOfToday]
  );
  const filterOpen = useMemo(
    () => ({
      ...baseFilter,
      status: { anyOf: UNPAID_STATUSES },
      dueDate: { gte: startOfToday }
    }),
    [startOfToday]
  );
  const filterPaid = useMemo(
    () => ({
      ...baseFilter,
      status: { anyOf: PAID_STATUSES }
    }),
    []
  );

  const sortByDueDateAsc = useMemo(
    () => ({ dueDate: ListFormsSortParameterValue.ASC }),
    []
  );
  const sortByPaidAtDesc = useMemo(
    () => ({ paidAt: ListFormsSortParameterValue.DESC }),
    []
  );

  const onRowClick = (payable: PayableRow) => {
    if (payable.id) {
      goToPayableDetails({ payableId: payable.id });
    }
  };

  // Once all three sections have settled, show one combined empty state if
  // none of them have anything — otherwise each renders (or hides) itself.
  const sectionState = useRef({ overdue: false, open: false, paid: false });
  const settledKeys = useRef(new Set<"overdue" | "open" | "paid">());
  const [showCombinedEmptyState, setShowCombinedEmptyState] = useState(false);
  const handleSettled =
    (key: "overdue" | "open" | "paid") => (hasItems: boolean) => {
      sectionState.current[key] = hasItems;
      settledKeys.current.add(key);
      if (settledKeys.current.size === 3) {
        const { overdue, open, paid } = sectionState.current;
        setShowCombinedEmptyState(!overdue && !open && !paid);
      }
    };

  return (
    <WSElement {...props}>
      <Route
        path={`${PATH_PAYMENT_HISTORY}/:payableId/deduction/scheduled/:deductionId`}
        component={ScheduledDeductionDetails}
      />
      <Route
        path={`${PATH_PAYMENT_HISTORY}/:payableId/deduction/completed/:deductionId`}
        component={CompletedDeductionDetails}
      />
      <Route
        path={routeHelpersClientPayableDetails.path}
        component={ClientPayableDetails}
        exact
      />

      <WSList gap="2XL">
        <ClientPayablesSection
          title="Overdue invoices"
          filter={filterOverdue}
          sort={sortByDueDateAsc}
          dateField="due"
          csvFileName="Overdue payables"
          onRowClick={onRowClick}
          onSettled={handleSettled("overdue")}
        />

        <ClientPayablesSection
          title="Open invoices"
          filter={filterOpen}
          sort={sortByDueDateAsc}
          dateField="due"
          csvFileName="Open payables"
          onRowClick={onRowClick}
          onSettled={handleSettled("open")}
        />

        <ClientPayablesSection
          title="Payment history"
          filter={filterPaid}
          sort={sortByPaidAtDesc}
          dateField="paid"
          csvFileName="Payment history"
          onRowClick={onRowClick}
          onSettled={handleSettled("paid")}
        />

        {showCombinedEmptyState && (
          <WSElement>
            <WSSectionToolbar title="Payment history" />
            <WSEmptyState
              type="documents"
              title="No invoices yet"
              description="Invoices from your contractors will show up here."
            />
          </WSElement>
        )}
      </WSList>
    </WSElement>
  );
};
